import { get as _get, map as _map } from 'lodash-es';
import { useMemo, Fragment } from 'react';
import { useQuery } from '@apollo/client/react';
import Link from 'next/link';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import EventIcon from '@mui/icons-material/Event';
import MessageIcon from '@mui/icons-material/Message';
import { makeStyles } from 'tss-react/mui';
import Avatar from '@mui/material/Avatar';
import { myGroupsQuery } from '../apollo/groups';
import { useAppContext } from '../contexts/app';
import { buildImageUrl } from '../utils/images';
import { sortGroupsByRelevance } from '../utils/sort-groups';
import SidebarGroupActions from './calendar/sidebar-group-actions';

const useStyles = makeStyles()((theme) => ({
  chips: {
    marginTop: 2,
    '& > *:not(:first-of-type)': {
      marginLeft: theme.spacing(0.5),
    },
  },
  upcomingChip: {
    marginTop: theme.spacing(0.5),
    '& > span': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  chipIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 20,
  },
  nameWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    height: '1em',
    width: '1em',
    marginRight: theme.spacing(0.75),
  },
}));

const HeaderGroupsMobile = () => {
  const { classes } = useStyles();
  const { isLoggedIn, loggedInUser } = useAppContext();

  const { data: groupsData, loading: groupsLoading } = useQuery(myGroupsQuery, {
    skip: !isLoggedIn,
  });
  const groups = useMemo(() => {
    if (!groupsData) {
      return [];
    }
    return sortGroupsByRelevance(
      _get(groupsData, 'me.groups', []),
      loggedInUser?.groupMembers,
    );
  }, [groupsData, loggedInUser]);

  return (
    <>
      <List>
        {groupsLoading ? (
          <ListItem>Loading...</ListItem>
        ) : _map(groups, (group) => {
          return (
            <ListItemButton component={Link} href={`/groups/${group.slug}`} key={group.id}>
                <ListItemText>
                  <div className={classes.nameWrapper}>
                    {group.image && (
                      <Avatar
                        alt={group.name}
                        src={buildImageUrl(group.image, '20x20')}
                        className={classes.avatar}
                      />
                    )}
                    {group.name}
                  </div>
                  {(group.upcomingEventsCount > 0 || group.recentPostsCount > 0) && (
                    <div className={classes.chips}>
                      {group.upcomingEventsCount > 0 && (
                        <Chip
                          size="small"
                          label={(
                            <Fragment>
                              <EventIcon className={classes.chipIcon} /> {group.upcomingEventsCount} upcoming
                            </Fragment>
                          )}
                          variant="outlined"
                          color="primary"
                          className={classes.upcomingChip}
                        />
                      )}
                      {group.recentPostsCount > 0 && (
                        <Chip
                          size="small"
                          label={(
                            <Fragment>
                              <MessageIcon className={classes.chipIcon} /> {group.recentPostsCount} recent
                            </Fragment>
                          )}
                          variant="outlined"
                          className={classes.upcomingChip}
                        />
                      )}
                    </div>
                  )}
                </ListItemText>
            </ListItemButton>
          );
        })}
      </List>
      <List dense>
        <SidebarGroupActions browseLabel="Browse All Groups" />
      </List>
    </>
  );
};

export default HeaderGroupsMobile;
